import React, { useEffect, useState } from 'react';
import { api } from 'lib/api';
import { API_ENDPOINTS } from 'constants/endpoints';
import { Gamepad2, Settings, ToggleLeft, ToggleRight, X } from 'lucide-react';
import type { Variant } from 'types/admin';
import { listRowNumber, LIST_TD_NO_CLASS, LIST_TH_NO_CLASS, LIST_TABLE_CLASS, LIST_THEAD_CLASS, LIST_TBODY_CLASS, LIST_TH_CLASS, LIST_TD_CLASS } from 'lib/list-table';

const DEFAULT_ASSETS: Record<string, { cardBackStyle: string; tableThemeColor: string; themeAudioUrl: string }> = {
  Classic: { cardBackStyle: 'Classic Crimson', tableThemeColor: '#0b2c4d', themeAudioUrl: 'sounds/theme-classic.mp3' },
  AK47: { cardBackStyle: 'Desert Gold', tableThemeColor: '#dfb76c', themeAudioUrl: 'sounds/theme-ak47.mp3' },
  Muflis: { cardBackStyle: 'Royal Purple', tableThemeColor: '#ca3e3e', themeAudioUrl: 'sounds/theme-muflis.mp3' },
};

function resolveDefaultAssets(variant: Variant) {
  const preset = DEFAULT_ASSETS[variant.code] || DEFAULT_ASSETS.Classic;
  return {
    cardBackStyle: variant.card_back_style || preset.cardBackStyle,
    tableThemeColor: variant.table_theme_color || preset.tableThemeColor,
    themeAudioUrl: variant.theme_audio_url || preset.themeAudioUrl,
    lobbyVisibilityFlag: variant.status === true,
  };
}

const VariantManagement = () => {
  const [variants, setVariants] = useState<Variant[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null);
  const [assetForm, setAssetForm] = useState({
    cardBackStyle: 'Classic Crimson',
    tableThemeColor: '#0b2c4d',
    themeAudioUrl: 'sounds/theme-classic.mp3',
    lobbyVisibilityFlag: true,
  });
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const fetchVariants = () => {
    api.get<Variant[]>(API_ENDPOINTS.ADMIN.VARIANTS.GET_ALL)
      .then(res => {
        if (res.success && res.data) {
          setVariants(res.data);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to fetch variants:', err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchVariants();
  }, []);

  const toggleVariant = (id: number) => {
    api.post(API_ENDPOINTS.ADMIN.VARIANTS.TOGGLE_STATUS, { id })
      .then(res => {
        if (res.success) {
          fetchVariants();
        }
      })
      .catch(err => {
        console.error('Failed to toggle variant:', err);
      });
  };

  const togglePrivateTable = (id: number) => {
    api.post(API_ENDPOINTS.ADMIN.VARIANTS.TOGGLE_PRIVATE_TABLE, { id })
      .then(res => {
        if (res.success) {
          fetchVariants();
        }
      })
      .catch(err => {
        console.error('Failed to toggle private-table flag:', err);
      });
  };

  const openAssetModal = (variant: Variant) => {
    setSelectedVariant(variant);
    setAssetForm(resolveDefaultAssets(variant));
    setSaveSuccess(false);
    setSaveError('');
  };

  const handleAssetSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedVariant) return;
    setSaving(true);
    setSaveError('');
    api.post(API_ENDPOINTS.ADMIN.VARIANTS.UPDATE_ASSETS, {
      id: selectedVariant.id,
      cardBackStyle: assetForm.cardBackStyle,
      tableThemeColor: assetForm.tableThemeColor,
      themeAudioUrl: assetForm.themeAudioUrl,
      lobbyVisibilityFlag: assetForm.lobbyVisibilityFlag,
    })
      .then((res) => {
        if (!res.success) {
          setSaveError(res.message || 'Failed to save assets');
          return;
        }
        setSaveSuccess(true);
        fetchVariants();
        setTimeout(() => {
          setSelectedVariant(null);
          setSaveSuccess(false);
        }, 1500);
      })
      .catch((err) => {
        console.error('Failed to save variant assets:', err);
        setSaveError('Failed to save assets — try again');
      })
      .finally(() => setSaving(false));
  };

  if (loading) return (
    <div className="flex h-64 items-center justify-center text-muted font-medium">
      Loading game variants...
    </div>
  );
  

  return (
    <div className="bg-card p-8 rounded-2xl shadow-sm border border-line animate-fadeIn">
      <div className="flex items-center gap-3 mb-6">
        <Gamepad2 className="text-brand-gold" size={24} />
        <h3 className="text-xl font-bold text-heading">Supported Variations</h3>
      </div>
      <p className="text-muted text-sm mb-6">
        Configure game variations, toggle visibility flags for beta-testing, and manage specialized visual/audio assets.
      </p>

      <div className="overflow-hidden border border-line rounded-xl">
        <table className={LIST_TABLE_CLASS}>
          <thead className={LIST_THEAD_CLASS}>
            <tr>
              <th className={`${LIST_TH_NO_CLASS} px-6 py-4`}>No.</th>
              <th className={LIST_TH_CLASS}>Variant Name</th>
              <th className={LIST_TH_CLASS}>Description</th>
              <th className={LIST_TH_CLASS}>Lobby Visibility</th>
              <th className={LIST_TH_CLASS}>Private Table</th>
              <th className={LIST_TH_CLASS}>Status</th>
              <th className={LIST_TH_CLASS}>Actions</th>
            </tr>
          </thead>
          <tbody className={LIST_TBODY_CLASS}>
            {variants.map((variant, index) => (
              <tr key={variant.id} className="hover:bg-subtle/50 transition-colors">
                <td className={`${LIST_TD_NO_CLASS} px-6 py-4`}>{listRowNumber(1, index, 0)}</td>
                <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm font-semibold text-heading`}>{variant.name}</td>
                <td className={`${LIST_TD_CLASS} text-sm text-muted max-w-md`}>{variant.description}</td>
                <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                  <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${variant.status ? 'bg-green-500/10 text-green-700 border border-green-200' : 'bg-red-500/10 text-red-700 border border-red-200'}`}>
                    {variant.status ? 'Visible (Beta Testing)' : 'Hidden (Inactive)'}
                  </span>
                </td>
                <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                  <button
                    type="button"
                    onClick={() => togglePrivateTable(variant.id)}
                    disabled={!variant.status}
                    className="flex items-center gap-2 group disabled:cursor-not-allowed disabled:opacity-50"
                    title={
                      !variant.status
                        ? 'Activate this variant to allow private tables'
                        : variant.private_table_allowed
                          ? 'Disallow this variant for private tables'
                          : 'Allow this variant for private tables'
                    }
                  >
                    {variant.status && variant.private_table_allowed ? (
                      <ToggleRight className="text-brand-gold" size={28} />
                    ) : (
                      <ToggleLeft className="text-muted group-hover:text-heading transition-colors" size={28} />
                    )}
                    <span className={`text-xs font-semibold ${variant.status && variant.private_table_allowed ? 'text-green-700' : 'text-muted'}`}>
                      {variant.status && variant.private_table_allowed ? 'Allowed' : 'Not Allowed'}
                    </span>
                  </button>
                </td>
                <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                  <button
                    type="button"
                    onClick={() => toggleVariant(variant.id)}
                    className="flex items-center gap-2 group"
                    title={variant.status ? 'Deactivate Variant' : 'Activate Variant'}
                  >
                    {variant.status ? (
                      <ToggleRight className="text-brand-gold" size={32} />
                    ) : (
                      <ToggleLeft size={32} />
                    )}
                    <span className={`text-xs font-semibold ${variant.status ? 'text-green-700' : 'text-muted'}`}>
                      {variant.status ? 'Active' : 'Inactive'}
                    </span>
                  </button>
                </td>
                <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                  <button
                    onClick={() => openAssetModal(variant)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-subtle hover:bg-subtle text-heading rounded-lg border border-line font-medium transition-all text-xs"
                  >
                    <Settings size={14} /> Configure Assets
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedVariant && (
        <div className="fixed inset-0 bg-brand-dark/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn">
          <div className="bg-card w-full max-w-lg rounded-2xl shadow-xl overflow-hidden animate-scaleIn border border-line">
            <div className="p-6 border-b border-line flex items-center justify-between bg-gradient-to-r from-brand-dark to-slate-900 text-white">
              <div>
                <h4 className="font-bold text-lg text-brand-gold">{selectedVariant.name}</h4>
                <p className="text-xs text-faint">Visual & Audio Asset Configurations</p>
              </div>
              <button 
                onClick={() => setSelectedVariant(null)}
                className="text-muted hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleAssetSave} className="p-6 space-y-6">
              {saveSuccess && (
                <div className="bg-green-500/10 text-green-700 px-4 py-3 rounded-lg border border-green-200 text-sm font-semibold flex items-center gap-2">
                  ✓ Assets and visibility updated successfully!
                </div>
              )}
              {saveError && (
                <div className="bg-red-500/10 text-red-700 px-4 py-3 rounded-lg border border-red-200 text-sm font-semibold">
                  {saveError}
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Card Back Style</label>
                <select
                  value={assetForm.cardBackStyle}
                  onChange={(e) => setAssetForm({ ...assetForm, cardBackStyle: e.target.value })}
                  className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border"
                >
                  <option value="Classic Crimson">Classic Crimson (Traditional)</option>
                  <option value="Desert Gold">Desert Gold (Royal)</option>
                  <option value="Royal Purple">Royal Purple (Muflis Premium)</option>
                  <option value="Cyber Blue">Cyber Blue (Modern)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Lobby Table Theme Color</label>
                <div className="flex gap-3">
                  <input
                    type="color"
                    value={assetForm.tableThemeColor}
                    onChange={(e) => setAssetForm({ ...assetForm, tableThemeColor: e.target.value })}
                    className="h-10 w-12 rounded border border-line p-1 cursor-pointer bg-card"
                  />
                  <input
                    type="text"
                    value={assetForm.tableThemeColor}
                    onChange={(e) => setAssetForm({ ...assetForm, tableThemeColor: e.target.value })}
                    className="flex-1 rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-2.5 border"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Soundtrack Asset URI</label>
                <input
                  type="text"
                  value={assetForm.themeAudioUrl}
                  onChange={(e) => setAssetForm({ ...assetForm, themeAudioUrl: e.target.value })}
                  className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-subtle rounded-xl border border-line">
                <div>
                  <h5 className="font-semibold text-sm text-heading">Beta-Testing Visibility</h5>
                  <p className="text-xs text-muted">If checked, this variant will be shown in the lobby for testers.</p>
                </div>
                <input
                  type="checkbox"
                  checked={assetForm.lobbyVisibilityFlag}
                  onChange={(e) => setAssetForm({ ...assetForm, lobbyVisibilityFlag: e.target.checked })}
                  className="h-5 w-5 rounded border-line text-brand-gold focus:ring-brand-gold cursor-pointer"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setSelectedVariant(null)}
                  className="flex-1 px-4 py-2.5 bg-subtle hover:bg-subtle text-body rounded-lg font-semibold border border-line transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2.5 bg-brand-gold hover:bg-opacity-95 text-brand-dark rounded-lg font-bold transition-all text-sm shadow-sm disabled:opacity-60"
                >
                  {saving ? 'Saving…' : 'Save Assets'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VariantManagement;
